import React from "react"
import DraggableMap from './location/DraggableMap'
import StandardButton from '../app_components/StandardButton'
import SettingsTextInput from '../form_sections/SettingsTextInput'
import Icon from '../app_components/Icon'
import OnboardingToast, { bindOnboardingToastHandlers } from '../onboarding_sections/OnboardingToast'
import { semantic, primitives } from '@lexody/design-tokens'
import { iconColorForTone } from '../form_sections/morphismIconColors'
import { usesDashFormVariant } from '../../utils/formVariant'

const DASH_ICON_COLORS = {
  teal: primitives.green['500'],
  violet: primitives.violet['500'],
};

class LocationSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      firstName: '',
      address: '',
      selectedAddress: '',
      lat: '',
      lng: '',
      loaded: false,
      suggestions: [],
      showSuggestions: false,
      searchTimeout: null,
      isSearching: false,
      toastMessage: null,
    }

    bindOnboardingToastHandlers(this)
    this.handleChange = this.handleChange.bind(this)
    this.handleFirstNameChange = this.handleFirstNameChange.bind(this)
    this.handleSelect = this.handleSelect.bind(this)
    this.handleSignupNext = this.handleSignupNext.bind(this)
    this.updateUser = this.updateUser.bind(this)
    this.getCurrentUserInfo = this.getCurrentUserInfo.bind(this)
    this.updateLatLng = this.updateLatLng.bind(this)
    this.searchNominatim = this.searchNominatim.bind(this)
    this.handleInputFocus = this.handleInputFocus.bind(this)
    this.handleInputBlur = this.handleInputBlur.bind(this)
  }

  updateLatLng(lat, lng) {
    this.setState({
      lat: lat,
      lng: lng
    }, () => this.updateUser())

  }

  handleChange(event) {
    const address = event.target.value
    this.setState({
      address,
      lat: '',
      lng: '',
      selectedAddress: ''
    })

    // Debounce search - wait 500ms after user stops typing
    if (this.state.searchTimeout) {
      clearTimeout(this.state.searchTimeout)
    }

    const timeout = setTimeout(() => {
      this.searchNominatim(address)
    }, 500)

    this.setState({ searchTimeout: timeout })
  }

  componentDidMount() {
    this.getCurrentUserInfo()
  }

  handleFirstNameChange(value) {
    this.setState({ firstName: value }, () => this.updateUser())
  }

  handleSignupNext() {
    if (!this.state.firstName.trim()) {
      this.showOnboardingToast('Please enter your first name.')
      return
    }

    if (!this.state.selectedAddress) {
      this.showOnboardingToast('Please choose your city from the list.')
      return
    }

    $.ajax({
      url: `../update_user`,
      dataType: 'JSON',
      type: 'POST',
      data: {
        user: {
          first_name: this.state.firstName.trim(),
          location: this.state.selectedAddress,
          lat: this.state.lat,
          lng: this.state.lng
        }
      },
      header: {
        'Content-Type': 'application/json'
      },
      success: () => {
        window.location.replace('/sign_up_languages')
      },
      error: () => {
        this.showOnboardingToast('Please enter your first name and choose your city.')
      }
    })
  }

  componentWillUnmount() {
    this.clearOnboardingToast()
    // Clean up timeouts
    if (this.state.searchTimeout) {
      clearTimeout(this.state.searchTimeout)
    }
    if (this.state.timeout) {
      clearTimeout(this.state.timeout)
    }
  }

  // Geocode a single address to get coordinates
  geocodeAddress(address) {
    const url = `https://nominatim.openstreetmap.org/search?` +
      `q=${encodeURIComponent(address)}&` +
      `format=json&` +
      `addressdetails=1&` +
      `limit=1&` +
      `countrycodes=us`

    return fetch(url, {
      headers: {
        'User-Agent': 'Lexody/1.0'
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data && data.length > 0) {
          const result = data[0]
          return {
            formattedAddress: result.display_name,
            lat: parseFloat(result.lat),
            lng: parseFloat(result.lon)
          }
        }
        return null
      })
      .catch(error => {
        console.error('Error geocoding address:', error)
        return null
      })
  }

  // Search OpenStreetMap Nominatim API for city suggestions
  searchNominatim(query, isGeocoding = false) {
    if (!query || query.length < 2) {
      this.setState({ suggestions: [], showSuggestions: false })
      return
    }

    this.setState({ isSearching: true })

    // Nominatim requires a user agent header and has rate limits
    // Limit to US cities - removed featuretype=city to get more results
    const url = `https://nominatim.openstreetmap.org/search?` +
      `q=${encodeURIComponent(query)}&` +
      `format=json&` +
      `addressdetails=1&` +
      `limit=${isGeocoding ? 1 : 10}&` +
      `countrycodes=us`


    fetch(url, {
      headers: {
        'User-Agent': 'Lexody/1.0' // Required by Nominatim
      }
    })
      .then(response => {
        return response.json()
      })
      .then(data => {
        
        if (isGeocoding && data && data.length > 0) {
          // If geocoding, update state directly
          const result = data[0]
          this.setState({
            lat: parseFloat(result.lat),
            lng: parseFloat(result.lon),
            isSearching: false
          }, () => this.updateUser())
        } else {
          // Filter for cities and format results - be more lenient with filtering
          const allResults = data || []
          
          // Show results that are cities, towns, or places in the US
          const citySuggestions = allResults
            .filter(result => {
              const isCity = result.type === 'city' || 
                           result.type === 'town' ||
                           result.type === 'administrative' ||
                           result.class === 'place' ||
                           (result.address && result.address.city) ||
                           (result.address && result.address.town)
              return isCity
            })
            .map(result => ({
              description: result.display_name,
              placeId: result.place_id,
              lat: parseFloat(result.lat),
              lng: parseFloat(result.lon),
              formattedAddress: result.display_name
            }))


          this.setState({
            suggestions: citySuggestions,
            showSuggestions: citySuggestions.length > 0,
            isSearching: false
          })
        }
      })
      .catch(error => {
        this.setState({
          suggestions: [],
          showSuggestions: false,
          isSearching: false
        })
      })
  }

  handleInputFocus() {
    if (this.state.suggestions.length > 0) {
      this.setState({ showSuggestions: true })
    }
  }

  handleInputBlur() {
    // Delay hiding suggestions to allow click events
    setTimeout(() => {
      this.setState({ showSuggestions: false })
    }, 200)
  }

  getCurrentUserInfo() {
    $.ajax({
      url: `../get_current_user`,
      dataType: 'JSON',
      type: 'GET',
      header: {
        'Content-Type': 'application/json'
      },
      success: (data) => {
        let user = JSON.parse(data.data.current_user)

        this.setState({
          firstName: user.first_name != null ? user.first_name : '',
          address: user.location != null ? user.location : '',
          selectedAddress: user.location != null ? user.location : '',
          lat: user.lat != null ? parseFloat(user.lat) : '',
          lng: user.lng != null ? parseFloat(user.lng) : '',
          loaded: true
        })
        if (user.location != null && user.lat === null) {
          // If user has location but no coordinates, geocode it
          this.searchNominatim(user.location, true)
        }
      },
      error: function(xhr, status, err) {
        console.log(err)
      }.bind(this)
    });
  }

  updateUser() {
    if (this.props.signup && !this.state.firstName.trim()) {
      return
    }

    const { timeout } = this.state;
    clearTimeout(timeout);
    this.setState({
      timeout: setTimeout(() => {
        let data = {
          user: {
            location: this.state.selectedAddress,
            lat: this.state.lat,
            lng: this.state.lng
          }
        }

        if (this.props.signup) {
          data.user.first_name = this.state.firstName.trim()
        }

        $.ajax({
          url: `../update_user`,
          dataType: 'JSON',
          type: 'POST',
          data: data,
          header: {
            'Content-Type': 'application/json'
          },
          success: (data) => {
            if (this.props.dashboard && this.props.onUpdated) {
              this.props.onUpdated()
            } else {
              this.getCurrentUserInfo()
            }
          },
          error: (xhr) => {
            console.log(xhr.responseJSON || xhr.statusText)
          }
        })
      }, 500)
    })

  }

  // When the user selects an autocomplete suggestion...
  handleSelect(suggestion) {
    this.setState({
      selectedAddress: suggestion.formattedAddress,
      address: suggestion.formattedAddress,
      lat: suggestion.lat,
      lng: suggestion.lng,
      showSuggestions: false
    }, () => this.updateUser())
  }

  renderCityField() {
    const inputProps = {
      value: this.state.address,
      onChange: this.handleChange,
      onFocus: this.handleInputFocus,
      onBlur: this.handleInputBlur,
      placeholder: this.props.signup ? 'Enter your city…' : 'Where do you explore from?',
    };

    const cityLabel = this.props.signup ? 'What city do you live in?' : 'City';

    if (usesDashFormVariant(this.props.variant)) {
      return (
        <div className="lex-dash-field lex-dash-field--teal lex-dash-field--dropdown">
          <label className="lex-dash-field__label">{cityLabel}</label>
          <div className="lex-dash-field__inputWrap">
            <input className="lex-dash-field__input" {...inputProps} />
            <span className="lex-dash-field__icon" aria-hidden="true">
              <Icon name="map-marker" size={16} color={DASH_ICON_COLORS.teal} solid />
            </span>
          </div>
          <p className="lex-dash-field__hint">Select your city from the list — we&apos;ll open the map next.</p>
          {this.state.isSearching && (
            <div className="lex-dash-dropdown">
              <div className="lex-dropdown__item lex-dropdown__item--empty">Searching…</div>
            </div>
          )}
          {this.state.showSuggestions && this.state.suggestions.length > 0 && (
            <div className="lex-dash-dropdown">
              <div className="lex-dropdown__list">
                {this.state.suggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.placeId || index}
                    onClick={() => this.handleSelect(suggestion)}
                    className="lex-dropdown__item"
                    role="option"
                  >
                    {suggestion.description}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="lex-field">
        <p className="lex-field__label">{cityLabel}</p>
        <div className="lex-dropdown" style={{ position: 'relative' }}>
          <div className="lex-input lex-input--grey">
            <span className="lex-input__icon" aria-hidden="true">
              <Icon name="map-marker" size={18} color={iconColorForTone('grey')} />
            </span>
            <input className="lex-input__control" {...inputProps} />
          </div>
          {this.state.isSearching && (
            <div
              className="lex-dropdown__panel"
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                right: 0,
                zIndex: 40,
                padding: 10,
                textAlign: 'center',
                color: semantic.greyscale.text.subtitle,
              }}
            >
              Searching…
            </div>
          )}
          {this.state.showSuggestions && this.state.suggestions.length > 0 && (
            <div
              className="lex-dropdown__panel"
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                right: 0,
                zIndex: 40,
              }}
            >
              <div className="lex-dropdown__list">
                {this.state.suggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.placeId || index}
                    onClick={() => this.handleSelect(suggestion)}
                    className="lex-dropdown__item"
                    role="option"
                  >
                    {suggestion.description}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.loaded) {
      return (
        <div className="lex-field">
          <p className="lex-field__label">City</p>
          <div className="lex-input lex-input--grey">
            <span className="lex-input__icon" aria-hidden="true">
              <Icon name="map-marker" size={18} color={iconColorForTone('grey')} />
            </span>
            <input className="lex-input__control" placeholder="Loading…" disabled />
          </div>
        </div>
      );
    }

    return (
      <div className="onboarding-signup-location">
        {this.props.signup && (
          <div className="lex-dash-card onboarding-page__section">
            <SettingsTextInput
              label="What is your name?"
              attribute="first_name"
              value={this.state.firstName}
              onChange={this.handleFirstNameChange}
              placeholder="First name"
              icon="user"
              tone="blue"
            />
          </div>
        )}

        {this.props.signup ? (
          <div className="lex-dash-card onboarding-page__section">
            {this.renderCityField()}
          </div>
        ) : (
          this.renderCityField()
        )}

        {!this.props.dashboard && this.state.lat != '' && (
          <div className="lex-dash-card onboarding-page__section onboarding-page__mapCard">
            <div className="onboarding-map-header">
              <div className="onboarding-map-header__icon" aria-hidden="true">
                <Icon name="location-point" size={18} color={primitives.pink['500']} solid />
              </div>
              <div className="onboarding-map-header__text">
                <h2 className="onboarding-map-header__title">Pin on map</h2>
                <p className="onboarding-map-header__subtitle">Drag the pin to refine your location</p>
              </div>
            </div>
            <div className="onboarding-page__map">
              <DraggableMap
                lat={this.state.lat}
                lng={this.state.lng}
                updateLatLng={this.updateLatLng}
              />
            </div>
          </div>
        )}

        {this.props.signup && (
          <div className="onboarding-page__footer">
            <OnboardingToast message={this.state.toastMessage} />
            <StandardButton
              text="Continue"
              variant="primary"
              fullWidth
              size="large"
              onClick={this.handleSignupNext}
            />
          </div>
        )}
      </div>
    );
  }

}


export default LocationSettings;
